import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

import { useNavigate } from "react-router-dom";
import { getData, setData } from "@/lib/indexed-db";
import { getDeviceId } from "@/lib/device-id";
import { graphQLClient } from "@/lib/client-graphql";
import { apiUploadPicture } from "@/lib/client-api";
import { sendMessageToServiceWorker } from "@/features/service-worker-message";

// 컨텍스트 초기값 타입 정의
// interface AuthContextProps {
//   user: any;
//   login: (body: { email: string; password: string }) => Promise<void>;
//   logout: () => Promise<void>;
//   signup: (body: {
//     name: string;
//     email: string;
//     password: string;
//     picture?: string;
//   }) => Promise<void>;
// }

// 일반적인 타입
type AuthProviderProps = {
  children: ReactNode;
};

// AuthContext 생성
// const AuthContext = createContext();
// const AuthContext = createContext<AuthContextProps | undefined>(undefined);
const AuthProviderContext = createContext<any>(undefined);

export function AuthProvider({ children, ...props }: AuthProviderProps) {
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  // const location = useLocation(); // 현재 경로 정보를 가져옴

  useEffect(() => {
    const checkUser = async () => {
      const userInfo = await loadUserInfo();
      if (userInfo) {
        // navigate("/main");
        // 현재 URL의 search 파라미터를 보존하면서 /main으로 이동
        const currentSearch = window.location.search;
        const currentHash = window.location.hash;
        const targetPath = `/main${currentSearch}${currentHash}`;
        navigate(targetPath);

        // 사용자 정보가 변경되었을 수 있으므로, 서버에서 최신 정보를 가져온다.
        const updatedUserInfo = await graphQLClient.getMyInfo(true);
        await saveUserInfo({ ...userInfo, ...updatedUserInfo });
      } else {
        navigate("/auth");
      }
    };

    checkUser();
  }, []);

  // useEffect(() => {
  //   if (user && location.pathname === "/") navigate("/main");
  //   if (!user && location.pathname === "/main") navigate("/");

  //   // eslint-disable-next-line
  // }, [user, location.pathname, navigate]); // location.pathname이나 navigate 함수가 변경될 때 실행

  const signup = async (body: {
    name: string;
    email: string;
    password: string;
  }) => {
    try {
      // 서버에 회원가입 요청.
      const user = await graphQLClient.signUp(body);
      // 회원가입 성공 시, 로그인 정보를 저장.
      await saveUserInfo(user);
      // 메인 페이지로 이동.
      navigate("/main");
    } catch (err) {
      throw err;
    }
  };

  const login = async (body: { email: string; password: string }) => {
    try {
      // 서버에 로그인.
      const user = await graphQLClient.signIn(body);
      // 로그인 성공 시, 로그인 정보를 저장.
      await saveUserInfo(user);
      // 메인 페이지로 이동.
      navigate("/main");
    } catch (err) {
      throw err;
    }
  };

  const logout = async () => {
    // 유저 정보 삭제.
    await saveUserInfo(null);
    // 로그아웃 후, 홈 페이지로 이동.
    navigate("/auth");
  };

  const changeName = async (body: { newName: string }) => {
    try {
      // 서버에 로그인.
      const user = await graphQLClient.changeName(body);
      // 로그인 성공 시, 로그인 정보를 저장.
      await updateUserInfo({ name: user.name });
    } catch (err) {
      throw err;
    }
  };

  const updateUserData = async (body: { newData: any }) => {
    try {
      // 서버에 로그인.
      const user = await graphQLClient.updateUserData(body);
      // 로그인 성공 시, 로그인 정보를 저장.
      await updateUserInfo({ data: user.data });
    } catch (err) {
      throw err;
    }
  };

  const updateUserDataField = async (body: { newData: any }) => {
    try {
      // 서버에 로그인.
      const user = await graphQLClient.updateUserDataField(body);
      // 로그인 성공 시, 로그인 정보를 저장.
      await updateUserInfo({ data: user.data });
    } catch (err) {
      throw err;
    }
  };

  const updateImage = async (file: File) => {
    try {
      if (user) {
        const updatedUserInfo = await apiUploadPicture(file, user.token);
        if (updatedUserInfo) {
          await saveUserInfo({ ...user, picture: updatedUserInfo.picture });
        }
      }
    } catch (err) {
      throw err;
    }
  };

  // 원하는 필드만 업데이트
  const updateUserInfo = async (
    updates: Partial<{
      name: string;
      email: string;
      picture: string;
      data: any;
      [key: string]: any;
    }>,
  ) => {
    try {
      if (user) {
        const updatedUser = { ...user, ...updates };
        await saveUserInfo(updatedUser);
        return updatedUser;
      }
      return null;
    } catch (err) {
      throw err;
    }
  };

  const saveUserInfo = async (user: any) => {
    try {
      console.log("saveUserInfo", user);
      if (user) {
        const userInfo = { ...user, deviceId: getDeviceId() };
        // IndexedDB에 저장.
        await setData("users", "info", userInfo);
        // Context에 저장.
        setUser(userInfo);
        // Session에 저장.
        syncSession(userInfo);
        // ServiceWorker에 메시지 전송.
        sendMessageToServiceWorker({
          type: "USER_INFO_CHANGED",
          user: userInfo,
          timestamp: Date.now(),
        });
        console.log("saveUserInfo: ", userInfo);
      } else {
        await setData("users", "info", null);
        console.log("saved to ", null);
      }

      // await sendMessageToServiceWorker({
      //   type: "USER_DATA",
      // });
      console.log("Send USER_DATA message to service-worker.");
    } catch (error) {
      console.error("Error saving user data:", error);
    }
  };

  const loadUserInfo = async (): Promise<any> => {
    try {
      // IndexedDB에서 로드.
      const result = await getData("users", "info");
      const userInfo = result?.data;
      // Context에 저장.
      setUser(userInfo);
      // Session에 저장.
      syncSession(userInfo);
      console.log("LoadUserInfo: ", userInfo);
      return userInfo;
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  };

  const syncSession = (userInfo: any) => {
    if (userInfo) {
      sessionStorage.setItem("user", JSON.stringify(userInfo));
    } else {
      sessionStorage.removeItem("user");
    }
  };

  const value = {
    user,
    login,
    logout,
    signup,
    changeName,
    updateImage,
    updateUserData,
    updateUserDataField,
  };

  return (
    <AuthProviderContext.Provider {...props} value={value}>
      {children}
    </AuthProviderContext.Provider>
  );
}

// 커스텀 훅
export const useAuth = () => {
  const context = useContext(AuthProviderContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
